/**
 * FloatingSidePanel - A generic wrapper that renders a floating panel to the
 * right of its children via @floating-ui/react. The panel is portalled outside
 * the DOM hierarchy so it can escape parent overflow/clipping, and an
 * IntersectionObserver fades it in/out as the reference area scrolls into view.
 *
 * Supports a priority-based layer system via React context. Any descendant can
 * call `useSidePanel().show('detail', <Content />)` to push higher-priority
 * content into the panel, which automatically falls back to lower layers when
 * dismissed.
 *
 * Priority (highest wins): detail > contextual > persistent
 */

import {
  autoUpdate,
  FloatingPortal,
  offset as offsetMiddleware,
  shift,
  useFloating,
} from '@floating-ui/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';

/* ── Priority layers ──────────────────────────────────────────────────── */

const PRIORITIES = {
  persistent: 0,
  contextual: 1,
  detail: 2,
} as const;

type SidePanelPriority = keyof typeof PRIORITIES;

/* ── Context ──────────────────────────────────────────────────────────── */

interface SidePanelContextValue {
  /** Show content at the given priority level. Replaces any existing content at that level. */
  show: (priority: SidePanelPriority, content: React.ReactNode) => void;
  /** Hide the content at the given priority level. */
  hide: (priority: SidePanelPriority) => void;
}

const SidePanelContext = createContext<SidePanelContextValue | null>(null);

/**
 * Hook for descendants to push/pop content into the floating side panel.
 *
 * ```tsx
 * const sidePanel = useSidePanel();
 * sidePanel.show('detail', <DetailView />);
 * sidePanel.hide('detail');
 * ```
 */
export function useSidePanel(): SidePanelContextValue {
  const ctx = useContext(SidePanelContext);
  if (!ctx) {
    throw new Error('useSidePanel must be used within a <FloatingSidePanel>');
  }
  return ctx;
}

/* ── Component ────────────────────────────────────────────────────────── */

interface FloatingSidePanelProps {
  /** The content area that the panel floats relative to. */
  children: React.ReactNode;
  /** Default persistent panel content. Rendered when no higher-priority layer is active. */
  panel: React.ReactNode;
  /** Horizontal gap between the reference area and the panel. @default 48 */
  offset?: number;
  /** Additional class names for the floating panel wrapper. @default "w-80" */
  panelClassName?: string;
  /** Top padding to clear fixed headers (px). @default 80 */
  topPadding?: number;
  /** Force-hide the panel (e.g. when a drawer is open). @default false */
  hidden?: boolean;
}

export const FloatingSidePanel: React.FC<FloatingSidePanelProps> = ({
  children,
  panel,
  offset = 48,
  panelClassName = 'w-80',
  topPadding = 80,
  hidden = false,
}) => {
  // Layer registry: map of priority → content
  const [layers, setLayers] = useState<Map<SidePanelPriority, React.ReactNode>>(
    () => new Map(),
  );

  const show = useCallback(
    (priority: SidePanelPriority, content: React.ReactNode) => {
      setLayers((prev) => {
        const next = new Map(prev);
        next.set(priority, content);
        return next;
      });
    },
    [],
  );

  const hide = useCallback((priority: SidePanelPriority) => {
    setLayers((prev) => {
      const next = new Map(prev);
      next.delete(priority);
      return next;
    });
  }, []);

  const contextValue = useMemo(() => ({ show, hide }), [show, hide]);

  // Resolve the highest-priority active layer, falling back to the `panel` prop
  const activeContent = useMemo(() => {
    let best: { priority: number; content: React.ReactNode } | undefined;
    layers.forEach((content, key) => {
      const p = PRIORITIES[key];
      if (best === undefined || p > best.priority) {
        best = { priority: p, content };
      }
    });
    return best !== undefined ? best.content : panel;
  }, [layers, panel]);

  // Track whether the reference area is visible in the viewport
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { refs, floatingStyles } = useFloating({
    placement: 'right-start',
    middleware: [
      offsetMiddleware(offset),
      shift({ padding: { top: topPadding, right: 16, bottom: 32, left: 16 } }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Merge IntersectionObserver ref with floating reference ref
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  return (
    <SidePanelContext.Provider value={contextValue}>
      <div ref={setRefs}>{children}</div>

      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={cn(
            'z-10 transition-all duration-300 ease-in-out',
            isVisible && activeContent && !hidden
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-2 pointer-events-none',
            panelClassName,
          )}
        >
          {activeContent}
        </div>
      </FloatingPortal>
    </SidePanelContext.Provider>
  );
};
